import React, { useCallback, useMemo, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

// LOCAL VARIABLES
import { VENDOR_OPTION_KEYS } from '../variables.js';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';
import { NEW_USED } from 'codes';
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL FUNCTIONS
import { apiFetch, makePath } from 'functions.js';

// GLOBAL HELPERS
import { useRecordContext } from 'helpers/getRecordData.js';
import useRecordView from 'helpers/getRecordView';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// STYLES
import styles from './addRepairInvoice.module.scss';

// VARIABLES
const ITEM_CONSTRUCTOR = () => ({
  recipient_name: '',
  recipient_email_address: '',
  recipient_phone_number: ''
});

// MAIN COMPONENT
const AddRepairInvoice = () => {

  // CONTEXT
  const { recordID } = useParams();
  const { values, setFieldValue  } = useFormikContext() || {};
  const { record: { inventory = {}, ...record } = {}, refetchRecord } = useRecordContext() || {};
  const { isEdit, isSales, isView } = useRecordView();

  // REFS
  const node = useRef();

  // STATE
  const [show, setShow] = useState(false);

  // MEMOS
  const {
    new_used
  } = useMemo(
    () => isSales ? inventory : isEdit ? values : record,
    [record, inventory, values, isEdit, isSales]
  );
  const isNew = useMemo(() => new_used === NEW_USED.new.numeric, [new_used]);

  // RENDER
  return (<>
    <Form.PlainText
      label="Repair Invoice for Payment"
      value={
        <Button.Add
          className={styles.addButton}
          onClick={() => setShow(!show)}
          size="sm"
          fullWidth={false}
          outline
        />
      }
    />
    <div className={clsx(show && styles.container)} ref={node} />
    {show && createPortal(
      <Form
        method="POST"
        endpoint={ENDPOINTS.inventory.repair.save(recordID)}
        initialValues={{
          daily_sales_inventory_id: recordID
        }}
        onSuccess={() => {
          if (isView) refetchRecord();
          else refetchRecord({
            onSuccess: ({ result }) => {
              const { aryRepairInvoice } = result;
              setFieldValue('aryRepairInvoice', aryRepairInvoice)
            }
          });
          setShow(false);
        }}
        loadingMessage="Saving repair invoice for payment"
        successMessage="Repair invoice for payment saved"
        errorMessage="Unable to save repair invoice for payment."
        className={styles.form}
      >
        <FormBody isNew={isNew} setShow={setShow} />
      </Form>,
      node.current
    )}
  </>);
}

const FormBody = ({ isNew, setShow }) => {
  
  // CONTEXT
  const { setFieldValue } = useFormikContext();

  // CALLBACK
  const handleVendor = useCallback(
    v =>
      apiFetch({
        method: 'GET',
        endpoint: makePath(ENDPOINTS.admin.vendor.single, v),
        onSuccess: ({ result }) => {
          setFieldValue('vendor_contact', result.vendor_contact);
          setFieldValue('vendor_email_address', result.vendor_email_address);
          setFieldValue('vendor_phone_number', result.vendor_phone_number);
        }
      }),
    [setFieldValue]
  );

  return (
    <Form.Body>
      <Form.Select
        label="Vendor Name"
        name="dsl_vendor_id"
        lookup="vendor"
        optionKeys={VENDOR_OPTION_KEYS}
        onSelect={handleVendor}
        required
      />
      <Form.Control label="Invoice #" name="invoice_number" required />
      <Form.Control label="Invoice Amount to be Paid" name="invoice_amount" schema="dollars" required />
      <Form.Textarea label="Comments" name="comments" />
      <Form.Control name="vendor_contact" label="Vendor Contact Name" required />
      <Form.Control name="vendor_email_address" label="Vendor Email" type="email" required />
      <Form.Control name="vendor_phone_number" label="Vendor Phone" schema="tel" required />
      <Attachment.Upload
        label="Invoice Signed by Authorized Manager"
        name="documentAttachment"
        documentType={isNew ?  INVENTORY_FILE_TYPE_IDS.REPAIR_INVOICE_NEW :  INVENTORY_FILE_TYPE_IDS.REPAIR_INVOICE_USED}
        uploadType="inventory"
        useTemp
        useDocUpr={false}
        required
      />

      <Form.Area label="Invoice Processed Confirmation To">
        <Form.Table
          name="aryAdditionalRecipient"
          itemLabel="Email Recipient"
          buttonBefore
          itemConstructor={ITEM_CONSTRUCTOR}
          className="p-0"
          button={{
            size: 'md'
          }}
        >
          {() => [
            <Form.Control name="recipient_name" label="Name" required />,
            <Form.Control name="recipient_email_address" label="Email" type="email" required />,
            <Form.Control name="recipient_phone_number" label="Phone" schema="tel" required />
          ]}
        </Form.Table>
      </Form.Area>
      <Form.Area label=" ">
        <div className={styles.buttons}>
          <Button.Cancel label="Cancel" onClick={() => setShow(false)} fullWidht={false} />
          <Button.Submit label="Save" fullWidht={false} />
        </div>
      </Form.Area>
    </Form.Body>
  );
};

// EXPORT
export default AddRepairInvoice;
